import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from './api';
import { LoginCredentials, RegisterData } from './types';

// Query keys
export const authKeys = {
  all: ['auth'] as const,
  profile: () => [...authKeys.all, 'profile'] as const,
};

// Fetch current user profile
export const useProfile = (enabled = true) => {
  return useQuery({
    queryKey: authKeys.profile(),
    queryFn: async () => {
      const response = await apiService.getCurrentUser();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error || 'Failed to fetch profile');
    },
    enabled: enabled && !!localStorage.getItem('accessToken'),
    staleTime: 10 * 60 * 1000, // 10 minutes - profile doesn't change often
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    retry: false, // Don't retry on auth errors
  });
};

// Login mutation
export const useLoginMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      const response = await apiService.login(credentials);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error || 'Login failed');
    },
    onSuccess: data => {
      // Set profile in cache
      queryClient.setQueryData(authKeys.profile(), data.user);
    },
  });
};

// Register mutation
export const useRegisterMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: RegisterData) => {
      const response = await apiService.register(data);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error || 'Registration failed');
    },
    onSuccess: data => {
      // Set profile in cache
      queryClient.setQueryData(authKeys.profile(), data.user);
    },
  });
};

// Logout mutation
export const useLogoutMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiService.logout();
    },
    onSuccess: () => {
      // Clear all auth-related queries
      queryClient.removeQueries({ queryKey: authKeys.all });
    },
  });
};

// Refresh user profile
export const useRefreshProfile = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: authKeys.profile() });
  };
};

// Social login mutation (Firebase ID token → backend JWT)
export const useSocialLoginMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (idToken: string) => {
      const response = await apiService.socialLogin(idToken);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error || 'Social login failed');
    },
    onSuccess: data => {
      queryClient.setQueryData(authKeys.profile(), data.user);
    },
  });
};
